import { Building2, Calendar, ChevronRight, Coins, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";

import { formatAmounts, formatEligibility } from "../../format";
import type { FinancialAidCard as FinancialAidCardT } from "../../types";

/**
 * Directory result card for a financial aid (OAC-441, étape 3). The funding type
 * leads as an accent chip — it is the first thing an enterprise sorts on ("grant
 * or loan?") — then title, then source → date → montant. "Voir la fiche" opens
 * the detail sheet (étape 4); the heart toggles the favourite (étape 6).
 */
export function FinancialAidCard({
  aid,
  onOpenDetail,
  onToggleFavorite,
  isToggling = false,
}: {
  aid: FinancialAidCardT;
  onOpenDetail: (aid: FinancialAidCardT) => void;
  onToggleFavorite: (aid: FinancialAidCardT) => void;
  isToggling?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="min-w-0 space-y-1">
        {aid.funding_types_display.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pb-0.5">
            {aid.funding_types_display.map((label) => (
              <span
                key={label}
                className="inline-flex items-center rounded-md bg-theme-economic-soft px-2 py-0.5 text-xs font-semibold text-theme-economic-ink"
              >
                {label}
              </span>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => onOpenDetail(aid)}
          className="text-left font-semibold leading-tight underline-offset-4 hover:underline"
        >
          {aid.title}
        </button>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Building2 className="size-3.5 shrink-0" aria-hidden />
            {aid.organization}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="size-3.5 shrink-0" aria-hidden />
            {formatEligibility(aid, t)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Coins className="size-3.5 shrink-0" aria-hidden />
            {formatAmounts(aid, t)}
          </span>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-border/70 pt-3">
        <button
          type="button"
          onClick={() => onOpenDetail(aid)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-theme-economic-ink underline-offset-4 hover:underline"
        >
          {t("financialAids.card.viewSheet")}
          <ChevronRight className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-pressed={aid.is_favorite}
          aria-label={
            aid.is_favorite
              ? t("financialAids.card.unfavorite")
              : t("financialAids.card.favorite")
          }
          disabled={isToggling}
          onClick={() => onToggleFavorite(aid)}
          className={cn(
            "grid size-9 place-items-center rounded-lg border transition-colors",
            aid.is_favorite
              ? "border-destructive/30 bg-destructive/10"
              : "border-border bg-background hover:bg-accent",
            isToggling && "opacity-60",
          )}
        >
          <Heart
            className={cn(
              "size-[18px]",
              aid.is_favorite
                ? "fill-destructive text-destructive"
                : "text-muted-foreground",
            )}
            aria-hidden
          />
        </button>
      </div>
    </Card>
  );
}
